import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { Tv1PickData, Tv2PickData } from '@cds/query-objects';
import { OptionQuery } from 'packages/sports/web/app/src/navigation-core/pick-uri.model';

import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { PickId, V1PickId, V2OptionMarketPickId, V2ParticipantPickId } from '../../../core/picks/pick-id';
import { PickType } from '../../../core/picks/pick-models';

@Injectable()
export default class BetslipPickOptionsService {
    convertFromPickIds(ids: PickId[]): OptionQuery[] {
        if (!ids.length) {
            return [];
        }

        const options: OptionQuery[] = [];

        ids.forEach((id) => {
            if (V1PickId.isId(id)) {
                options.push({
                    fixture: id.fixtureId,
                    optionGroup: id.marketId,
                    option: id.optionId,
                });
            }

            if (V2OptionMarketPickId.isId(id)) {
                options.push({
                    fixture: id.fixtureId,
                    optionGroup: id.optionMarketId,
                    option: id.optionId,
                });
            }

            if (V2ParticipantPickId.isId(id)) {
                options.push({
                    fixture: id.fixtureId,
                    optionGroup: id.marketId,
                    option: id.participants[0],
                });
            }
        });

        return options;
    }

    convertFromPicks(picks: BetslipPick[]): OptionQuery[] {
        if (!picks.length) {
            return [];
        }

        const pickParams: OptionQuery[] = [];

        picks.forEach((pick) => {
            if (BetslipGroupPick.isPick(pick)) {
                const tv1Legs: Tv1PickData[] = [];
                const tv2Legs: Tv2PickData[] = [];
                for (const leg of pick.getLegs()) {
                    if (V1PickId.isId(leg.id)) {
                        const tv1Leg = leg as BetslipV1Pick;
                        tv1Legs.push({
                            fixtureId: tv1Leg.event.id,
                            pickGroupId: pick.groupInfo.groupId,
                            gameId: tv1Leg.market.id,
                            resultId: tv1Leg.option.id,
                        });
                    }
                    if (V2OptionMarketPickId.isId(leg.id)) {
                        const tv2Leg = leg as BetslipV2OptionMarketPick;
                        tv2Legs.push({
                            fixtureId: tv2Leg.fixture.fixtureId,
                            pickGroupId: pick.groupInfo.groupId,
                            optionMarketId: tv2Leg.market.id,
                            optionId: tv2Leg.option.id,
                            isClassicBetBuilder: false,
                        });
                    }
                    if (V2ParticipantPickId.isId(leg.id)) {
                        throw new Error(`Participant markets are not supported (yet)`);
                    }
                }
                pickParams.push({
                    tv1Legs,
                    tv2Legs,
                });
            }

            if (BetslipV1Pick.isPick(pick)) {
                pickParams.push({
                    fixture: pick.event.id,
                    option: pick.option.id,
                    optionGroup: pick.market.id,
                });
            }
            if (BetslipV2OptionMarketPick.isPick(pick)) {
                pickParams.push({
                    fixture: pick.fixture.fixtureId,
                    option: pick.option.id,
                    optionGroup: pick.market.id,
                    priceId: pick.boostedPriceDetails?.isPriceBoosted ? pick.boostedPriceDetails?.boostedPriceId! : pick.acceptedPrice.price?.id,
                });
            }
            if (BetslipV2ParticipantPick.isPick(pick)) {
                pickParams.push({
                    fixture: pick.fixture.fixtureId,
                    option: pick.participants[0].fixtureParticipantId,
                    optionGroup: pick.market.id,
                });
            }

            if (pick.id.getPickType() === PickType.BetBuilderPick) {
                pickParams[pickParams.length - 1].offerSource = OfferSource.BetBuilder;
            }
        });

        return pickParams;
    }
}
